/**
 * CemeteryPicker — typeahead input that lets the submitter pick a
 * cemetery from our directory. Writes `cemetery_slug` (and a display
 * `cemetery_name`) on the memorial so the cemetery detail page can
 * auto-populate its "Memorials at this site" list.
 *
 * Uses the public `GET /api/cemeteries?q=` endpoint — cached to 200ms
 * debounce so typing is snappy. If no directory match is found the
 * user can still type a free-text burial place; both fields survive.
 */
import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { MapPin, Check, X } from "lucide-react";

export default function CemeteryPicker({
  value,           // string — currently linked cemetery_slug
  displayValue,    // string — display name (fallback if user typed free-text)
  onChange,        // (slug: string | null, name: string) => void
  placeholder = "Search cemeteries · e.g. Sewri Mumbai, Kirkee Pune…",
  className = "",
  testidPrefix = "cemetery-picker",
}) {
  const [query, setQuery]       = useState(displayValue || "");
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [linked, setLinked]     = useState(!!value);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Reset local state if the parent props flip.
  useEffect(() => {
    setLinked(!!value);
    if (displayValue) setQuery(displayValue);
  }, [value, displayValue]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const runSearch = (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    http.get(`/cemeteries?q=${encodeURIComponent(q.trim())}&limit=8`)
      .then(({ data }) => setResults(data.items || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setLinked(false);
    // Passing null slug tells the parent "user is typing free-text"; the
    // display name still flows through so the form doesn't lose data.
    onChange?.(null, v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 200);
  };

  const select = (cem) => {
    const label = `${cem.name}${cem.city ? `, ${cem.city}` : ""}`;
    setQuery(label);
    setLinked(true);
    setOpen(false);
    onChange?.(cem.slug, label);
  };

  const clear = () => {
    setQuery("");
    setLinked(false);
    setResults([]);
    onChange?.(null, "");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testidPrefix}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60" />
        <input
          type="text"
          value={query}
          onChange={onInput}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 rounded-lg border text-sm bg-transparent"
          style={{ borderColor: linked ? "#16A34A" : "var(--wofy-border)" }}
          data-testid={`${testidPrefix}-input`}
        />
        {linked && (
          <span
            className="absolute right-9 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 text-[10px] font-mono uppercase"
            style={{ color: "#16A34A" }}
            data-testid={`${testidPrefix}-linked-badge`}
          >
            <Check className="w-3 h-3" /> Linked
          </span>
        )}
        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear cemetery"
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            data-testid={`${testidPrefix}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && (query.length >= 2) && (
        <div
          className="absolute left-0 right-0 top-full mt-1 wofy-card p-1 z-30 max-h-72 overflow-y-auto shadow-lg"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={`${testidPrefix}-dropdown`}
        >
          {loading && <div className="p-2 text-xs opacity-60">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-2 text-xs opacity-60">
              No cemetery match. Your entry will be saved as free text.
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => select(c)}
              className="block w-full text-left px-3 py-2 rounded hover:bg-slate-50 text-xs"
              data-testid={`${testidPrefix}-option-${c.slug}`}
            >
              <div className="font-medium">{c.name}</div>
              <div className="opacity-60 text-[10px]">
                {[c.city, c.state, c.country].filter(Boolean).join(" · ")} · {c.religion}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
